import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import api from "../../lib/api";
import { Loading } from "../../components/shared";
import { Input } from "../../components/ui/input";
import RazorpayCheckoutButton from "../../components/RazorpayCheckoutButton";

export default function ResidentFinanceDetail({ kind }) {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");

  useEffect(() => {
    api
      .get(`/resident/finance/${kind}/${id}`)
      .then((response) => {
        setItem(response.data);
        setPaymentAmount(String(response.data.outstanding || ""));
      })
      .catch((requestError) => {
        setError(
          requestError.response?.data?.detail || "This financial record is unavailable.",
        );
      });
  }, [id, kind]);

  if (error) return <p data-testid="resident-finance-detail-error">{error}</p>;
  if (!item) return <Loading />;

  const isBill = kind === "bills";
  const pdfPath = isBill ? "bills" : "receipts";
  const canDownloadPdf = isBill || item.source !== "FRESH_FINANCE";
  const canPayOnline = isBill && item.source === "FRESH_FINANCE" && item.outstanding > 0;

  return (
    <div data-testid={`resident-${isBill ? "bill" : "receipt"}-detail`}>
      <h1 className="break-words font-display text-3xl font-bold">
        {isBill ? item.bill_number : item.receipt_number}
      </h1>
      <p className="mt-2 break-words text-sm text-slate-600">
        {item.status} · {item.due_date || item.date}
      </p>
      <section
        className="mt-6 border border-slate-200 bg-white p-5"
        data-testid="resident-finance-detail-amount"
      >
        <p>Amount: ₹{item.total ?? item.amount}</p>
        {isBill && <p className="mt-2">Outstanding: ₹{item.outstanding}</p>}
        {item.advance_credit ? (
          <p className="mt-2">Advance credit: ₹{item.advance_credit}</p>
        ) : null}
      </section>
      {isBill && item.payment?.enabled && (
        <section
          className="mt-4 border border-emerald-200 bg-emerald-50 p-5"
          data-testid="resident-fresh-payment-details"
        >
          <h2 className="font-display text-lg font-semibold">Payment details</h2>
          <p className="mt-2 text-sm">Payee: {item.payment.payee_name}</p>
          <p className="mt-1 text-sm">UPI ID: {item.payment.upi_id}</p>
          <p className="mt-1 text-sm">Reference: {item.payment.transaction_reference}</p>
          {item.payment.upi_uri && (
            <a
              className={[
                "mt-4 inline-block border border-emerald-700 px-4 py-2 text-sm",
                "font-semibold text-emerald-800",
              ].join(" ")}
              data-testid="resident-fresh-payment-link"
              href={item.payment.upi_uri}
            >
              Pay exact amount by UPI
            </a>
          )}
        </section>
      )}
      {canPayOnline && (
        <section
          className="mt-4 border border-sky-200 bg-sky-50 p-5"
          data-testid="resident-online-payment"
        >
          <h2 className="font-display text-lg font-semibold">Pay online</h2>
          <p className="mt-1 text-sm text-slate-600">
            Choose the full due or enter a custom amount. Non-maintenance dues are settled first.
          </p>
          <label className="mt-4 block text-sm font-medium text-slate-700" htmlFor="resident-payment-amount">
            Payment amount
          </label>
          <Input
            data-testid="resident-payment-amount"
            id="resident-payment-amount"
            max={item.outstanding}
            min="1"
            onChange={(event) => setPaymentAmount(event.target.value)}
            step="0.01"
            type="number"
            value={paymentAmount}
          />
          <div className="mt-4">
            <RazorpayCheckoutButton
              amount={paymentAmount}
              billId={item.bill_id}
              onPaymentComplete={() => window.location.reload()}
              societyName="MySocietyMitra"
              testId="resident-bill-payment-button"
            />
          </div>
        </section>
      )}
      {canDownloadPdf && (
        <a
          className="mt-5 inline-block border border-slate-300 px-4 py-2 text-sm"
          data-testid={`resident-${isBill ? "bill" : "receipt"}-pdf-download`}
          href={`${process.env.REACT_APP_BACKEND_URL}/api/resident/finance/${pdfPath}/${id}/pdf`}
          rel="noreferrer"
          target="_blank"
        >
          Download {isBill ? "bill" : "receipt"} PDF
        </a>
      )}
    </div>
  );
}